import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateNewsItemMutation,
  useGetNewsItemQuery,
  useUpdateNewsItemMutation
} from '../../../../../services/endpoints/website/newsItem';
import { getCurrentUser } from '../../../../../services/helper';
import { ImageDetails, NewsItem } from '../../../../../types/website.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { NewsItemForm } from './NewsItemForm';
import { userCanEditCreateDelete_NewsItems } from './NewsItemsView';

export interface FormValues extends ImageDetails {
  id?: number;
  name: string;
  details: string;
  published_on: string;
  news_item_links: {
    text: string;
    target: string;
    link_type: string;
  }[];
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditNewsItemProps {
  editing?: boolean;
}

export const NewsItemLinkSchema = Yup.object({
  text: Yup.string().optional(),
  target: Yup.string().optional(),
  link_type: Yup.string().oneOf(['article', 'video']).optional()
});

export const AddEditNewsItem: React.FC<AddEditNewsItemProps> = ({ editing }) => {
  const params = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const [createNewsItem] = useCreateNewsItemMutation();
  const [updateNewsItem] = useUpdateNewsItemMutation();
  const { data, error, isLoading } = useGetNewsItemQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    details: '',
    published_on: '',
    news_item_links: [],
    image_file_name: ''
  });

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    details: Yup.string().required('Details is required'),
    image: Yup.string().when('image_file_name', {
      is: (val: string) => !val,
      then: (schema) => schema.required('Image is required'),
      otherwise: (schema) => schema.optional(),
    }),
    published_on: Yup.string().required('Published On is required'),
    news_item_links: Yup.array().of(NewsItemLinkSchema).optional()
  });

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading News Item', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.newsItem) {
      const { newsItem } = data;
      setInitialValues({
        id: newsItem.id,
        name: newsItem.name,
        details: newsItem.details,
        image_file_name: newsItem.image_file_name,
        published_on: newsItem.published_on,
        news_item_links: newsItem.news_item_links
      });
    }

    if (!userCanEditCreateDelete_NewsItems(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/website/news-items`);
    }
  }, [data]);

  const handleSubmit = async (formValue: FormValues) => {
    try {
      const newsItemData: NewsItem = {
        ...formValue,
        image: formValue.image ? formValue.image : undefined,
        published_on: dateToUTC(new Date(formValue.published_on), 'MM/dd/yyyy')
      };
      delete newsItemData.image_file_name;
      if (editing) {
        const res = await updateNewsItem(newsItemData).unwrap();
        if (res && res.newsItem) {
          addToast(`News Item updated successfully`, 'success');
          navigate(`/admin/website/news-items/${data?.newsItem.id}`);
        }
      } else {
        const res = await createNewsItem(newsItemData).unwrap();
        if (res && res.newsItem) {
          navigate(`/admin/website/news-items/${res.newsItem.id}`);
          addToast('News Item created successfully', 'success');
        } else throw new Error('A problem happened while creating News Item');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
      <FadeIn className="p-lg-4">
        <Stack
            gap={3}
            className="py-lg-0 px-4 py-2"
        >
          <div>
            <BackButton
                text={`Back to News Item${editing ? ' details' : 's'}`}
                color="#2B4E64"
                url={
                  editing
                      ? `/admin/website/news-items/${data?.newsItem.id}`
                      : '/admin/website/news-items'
                }
            />
            <h2
                className="d-none d-lg-block text-info fw-bold mb-0"
                style={{ fontSize: '2.25rem' }}
            >
              {editing ? 'Edit' : 'New'} News Item
            </h2>
          </div>

          <div
              className="p-lg-5 p-3"
              style={{
                boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
                border: '1px solid #EBEBEB',
                borderRadius: 10
              }}
          >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
              {({
                  submitForm,
                  isSubmitting,
                  dirty,
                  touched,
                  errors,
                  values,
                  setFieldValue
                }: FormikProps<FormValues>) => (
                  <FormikForm className="text-start">
                    <div style={{ minHeight: '65vh' }}>
                      <NewsItemForm
                          errors={errors}
                          touched={touched}
                          values={values}
                          setFieldValue={setFieldValue}
                      />
                    </div>

                    <Row className="justify-content-end mt-3">
                      <Col
                          xs={6}
                          lg={3}
                      >
                        <SimpleButton
                            disabled={isSubmitting}
                            type="button"
                            onClick={() => {
                              if (editing && !dirty) {
                                addToast('No changes to save', 'info');
                                navigate(`/admin/website/news-items/${data?.newsItem.id}`);
                                return;
                              }
                              submitForm();
                            }}
                            className="w-100"
                            variant="primary"
                        >
                          {editing ? 'Save' : 'Create'}
                        </SimpleButton>
                      </Col>
                    </Row>
                  </FormikForm>
              )}
            </Formik>
          </div>
        </Stack>
      </FadeIn>
  );
};